import { CalendarMonthOutlined, CheckCircleOutlined, PercentOutlined, ScheduleOutlined } from '@mui/icons-material';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
    gap: 2,
    mb: 2.5,
  },
  card: {
    p: 2.25,
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    borderRadius: 2,
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    border: `1px solid ${colors.GRAY_HR}`,
  },
  iconWrap: tint => ({
    width: 42,
    height: 42,
    borderRadius: 1.25,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    background: `${tint}1A`,
    color: tint,
  }),
  value: { fontSize: 22, fontWeight: 700, color: colors.BLACK, lineHeight: 1.1 },
  label: { fontSize: 12.5, color: colors.GRAY_DARK, mt: 0.5 },
};

/**
 * Pure presentational. KPIs come precomputed from the backend
 * (see `useSessionsStatsApi`). The component renders, it does not aggregate.
 */
export default function SessionsKpis({ stats, loading }) {
  const { t2 } = useTranslation('EditCourseSessions');

  const items = [
    {
      key: 'total',
      value: stats?.totalSessions ?? 0,
      label: t2('kpis.total'),
      icon: <CalendarMonthOutlined />,
      tint: colors.GREEN,
    },
    {
      key: 'completed',
      value: stats?.completedSessions ?? 0,
      label: t2('kpis.completed'),
      icon: <CheckCircleOutlined />,
      tint: colors.GREEN_LEAF,
    },
    {
      key: 'scheduled',
      value: stats?.scheduledSessions ?? 0,
      label: t2('kpis.scheduled'),
      icon: <ScheduleOutlined />,
      tint: colors.BLUE,
    },
    {
      key: 'averageAttendance',
      value: `${stats?.averageAttendance ?? 0}%`,
      label: t2('kpis.averageAttendance'),
      icon: <PercentOutlined />,
      tint: colors.BROWN,
    },
  ];

  return (
    <Box sx={styles.grid}>
      {items.map(item => (
        <Card key={item.key} sx={styles.card} elevation={0}>
          <Box sx={styles.iconWrap(item.tint)}>{item.icon}</Box>
          <Box>
            {loading && !stats ? (
              <Skeleton variant="text" width={60} height={28} />
            ) : (
              <Typography sx={styles.value}>{item.value}</Typography>
            )}
            <Typography sx={styles.label}>{item.label}</Typography>
          </Box>
        </Card>
      ))}
    </Box>
  );
}

SessionsKpis.propTypes = {
  stats: PropTypes.shape({
    totalSessions: PropTypes.number,
    completedSessions: PropTypes.number,
    scheduledSessions: PropTypes.number,
    averageAttendance: PropTypes.number,
  }),
  loading: PropTypes.bool,
};

SessionsKpis.defaultProps = { stats: null, loading: false };
